// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import {
  Call,
  CallAgent,
  RemoteParticipant,
  PermissionConstraints,
} from "@azure/communication-calling";
import {
  GroupCallLocator,
  TeamsMeetingLinkLocator,
} from "@azure/communication-calling";
import {
  CommunicationUserIdentifier,
  AzureCommunicationTokenCredential,
} from "@azure/communication-common";
import {
  CallAdapter,
  CallAdapterState,
  FluentThemeProvider,
  DEFAULT_COMPONENT_ICONS,
  CallClientProvider,
  CallAgentProvider,
  CallProvider,
  createAzureCommunicationCallAdapter,
  JoinCallOptions,
  useAzureCommunicationCallAdapter,
} from "@azure/communication-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createAutoRefreshingCredential } from "../utils/credential";
import MobileDetect from "mobile-detect";
import CallingComponents from "./CallComponentsStateful";
import { registerIcons } from "@fluentui/react";
import { useId, useBoolean } from "@fluentui/react-hooks";
import { Dialog, DialogType } from "@fluentui/react/lib/Dialog";
import { ContextualMenu } from "@fluentui/react/lib/ContextualMenu";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { useGlobalState } from "../utils/GlobalState";
import { setTimeout } from "timers";
import { IStudentDetails } from "../models/StudentDetails";
import { StudentService } from "../services/student.service";
import { useMsal } from "@azure/msal-react";

const detectMobileSession = (): boolean =>
  !!new MobileDetect(window.navigator.userAgent).mobile();

export interface CallScreenProps {
  token: string;
  userId: CommunicationUserIdentifier;
  groupId: string;
  callLocator: GroupCallLocator | TeamsMeetingLinkLocator;
  displayName: string;
  webAppTitle: string;
  onCallEnded: () => void;
  onParticipantsJoined: () => void;
}

export const CallScreen = (props: CallScreenProps): JSX.Element => {
  const { instance } = useMsal();
  registerIcons({ icons: DEFAULT_COMPONENT_ICONS });
  const { token, userId, callLocator, displayName, webAppTitle, onCallEnded } =
    props;
  //const [adapter, setAdapter] = useState<CallAdapter>();
  const callIdRef = useRef<string>();
  const adapterRef = useRef<CallAdapter>();
  const [call, setCall] = useState<Call>();
  const [studentjoinedDialog, { toggle: toggleStudentJoinedDialog }] =
    useBoolean(true);
  const { state } = useGlobalState();

  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");
  const dialogStyles = { main: { maxWidth: 450 } };
  const [isDraggable] = useBoolean(false);
  const [studentNumber, setStudentNumber] = useState<string>("");
  const [studentDetails, setStudentDetails] = useState<IStudentDetails>();

  const studentService = new StudentService(instance);

  const dragOptions = {
    moveMenuItemText: "Move",
    closeMenuItemText: "Close",
    menu: ContextualMenu,
    keepInBounds: true,
  };

  const joinedDialogProps = {
    type: DialogType.normal,
    title: "Student Joined",
    subText: `Student ${studentNumber} has joined.`,
  };

  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: false,
      styles: dialogStyles,
      dragOptions: isDraggable ? dragOptions : undefined,
    }),
    [isDraggable, labelId, subTextId]
  );

  const getStudentDetails = (id: string) => {
    studentService.getStudentDetails(id).then((s) => {
      setStudentDetails(s);
    });
  };

  const credential = useMemo(
    () => new AzureCommunicationTokenCredential(token),
    [token]
  );
  const afterCreate = async (adapter: CallAdapter): Promise<CallAdapter> => {
    // console.log("callLocator",props.callLocator);
    if (adapter) {
      //Events
      adapter.on("callEnded", () => {
        onCallEnded();
      });
      adapter.on("participantsJoined", (event) => {
        console.log("Participants joined event", event);
        let participants: RemoteParticipant[] = event.joined;
        let student: RemoteParticipant = participants[0];
        if (student && student.displayName) {
          let id: string = student.displayName.split(" ")[1];
          console.log("Student joined event.");

          setStudentNumber(id);
          toggleStudentJoinedDialog();
          getStudentDetails(id);
        }
      });
      // adapter.on('displayNameChanged', (e) => {
      //   console.log("Display name changed event", e);

      //   //always expecting the format {Student 123456}, so if we split and take will be 123456
      //   let id:string = e.displayName.split(" ")[1];
      //   setStudentNumber(id);
      //   toggleStudentJoinedDialog();
      //   getStudentDetails(id);
      // });
      adapter.on("error", (e) => {
        // Error is already acted upon by the Call composite, but the surrounding application could
        // add top-level error handling logic here (e.g. reporting telemetry).
        console.log("Adapter error event:", e);
      });
      adapter.onStateChange((state: CallAdapterState) => {
        const pageTitle = convertPageStateToString(state);
        document.title = `${pageTitle} - ${webAppTitle}`;

        callIdRef.current = state?.call?.id;
        // console.log("Call ID:", callIdRef.current);
      });

      // Devices
      let constrain: PermissionConstraints = { audio: true, video: true };
      adapter.askDevicePermission(constrain).then((permission) => {
        console.log("Permissions", permission);
      });
      adapter.queryCameras().then((cameras) => {
        console.log("Cameras", cameras);
      });

      // Start call
      let joinCallOptions: JoinCallOptions = {
        microphoneOn: false,
        cameraOn: false,
      };
      setCall(adapter.joinCall(joinCallOptions));
    }

    return adapter;
  };

  const leaveCall = async (adapter: CallAdapter): Promise<void> => {
    await adapter.leaveCall(true).catch((e) => {
      console.error("Failed to leave call", e);
    });
  };

  //console.log("calllocator", callLocator);
  const adapter = useAzureCommunicationCallAdapter(
    {
      userId,
      displayName,
      credential,
      locator: callLocator,
    },
    afterCreate,
    leaveCall
  );

  // useEffect(() => {
  //   (async () => {
  //     const adapter = await createAzureCommunicationCallAdapter({
  //       userId,
  //       displayName,
  //       credential: createAutoRefreshingCredential({ communicationUserId : userId.communicationUserId }, token),
  //       locator: callLocator
  //     });
  //     adapter.on('callEnded', () => {
  //       onCallEnded();
  //     });
  //     adapter.on('participantsJoined', (event) => {
  //       console.log("Participants joined event", event);
  //       let participants: RemoteParticipant[] = event.joined;
  //       let student: RemoteParticipant = participants[0];
  //       if (student && student.displayName) {
  //         let id:string = student.displayName.split(" ")[1];
  //         console.log("Student joined:", id);

  //         setStudentNumber(id);
  //         toggleStudentJoinedDialog();
  //         getStudentDetails(id);
  //       }
  //     });

  //     adapter.on('displayNameChanged', (e) => {
  //       console.log("Display name changed event", e);

  //       //always expecting the format {Student 123456}, so if we split and take will be 123456
  //       let id:string = e.displayName.split(" ")[1];
  //       setStudentNumber(id);
  //       toggleStudentJoinedDialog();
  //       getStudentDetails(id);
  //     });

  //     adapter.on('error', (e) => {
  //       // Error is already acted upon by the Call composite, but the surrounding application could
  //       // add top-level error handling logic here (e.g. reporting telemetry).
  //       console.log('Adapter error event:', e);
  //     });
  //     adapter.onStateChange((state: CallAdapterState) => {
  //       const pageTitle = convertPageStateToString(state);
  //       document.title = `${pageTitle} - ${webAppTitle}`;

  //       callIdRef.current = state?.call?.id;
  //     });
  //     setAdapter(adapter);
  //     adapterRef.current = adapter;

  //     let joinCallOptions: JoinCallOptions ={ microphoneOn: true, cameraOn: true}

  //     setCall(adapter.joinCall(joinCallOptions));

  //     adapter.queryCameras().then((cameras) => {console.log("Cameras", cameras)});

  //   })();

  //   return () => {
  //     adapterRef?.current?.dispose();
  //   };
  // }, [callLocator, displayName, token, userId, onCallEnded]);

  return (
    <>
      <FluentThemeProvider>
        {adapter && (
          <CallClientProvider callClient={(adapter as any).callClient}>
            {adapter && (
              <CallAgentProvider callAgent={(adapter as any).callAgent}>
                {call && (
                  <CallProvider call={call}>
                    <CallingComponents
                      onCallEnded={() => onCallEnded()}
                      Details={state.examDetails!}
                      StudentDetails={studentDetails}
                    />
                  </CallProvider>
                )}
              </CallAgentProvider>
            )}
          </CallClientProvider>
        )}
      </FluentThemeProvider>

      <Dialog
        hidden={studentjoinedDialog}
        onDismiss={toggleStudentJoinedDialog}
        dialogContentProps={joinedDialogProps}
        modalProps={modalProps}
      >
        <div style={{ textAlign: "right" }}>
          <PrimaryButton
            onClick={() => {
              toggleStudentJoinedDialog();
            }}
            text="Ok"
          />
        </div>
      </Dialog>
    </>
  );
};

const convertPageStateToString = (state: CallAdapterState): string => {
  switch (state.page) {
    case "accessDeniedTeamsMeeting":
      return "error";
    case "leftCall":
      return "end call";
    case "removedFromCall":
      return "end call";
    default:
      return `${state.page}`;
  }
};
